import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import useSoftwareService from '../../hooks/useSoftwareService';
import { createInitialWizardState } from '../../config/wizardSteps';
import { detailToWizardState } from '../../utils/wizardStateMapper';
import { apiErrorMessage } from '../../utils/responseHelpers';
import WizardStepViewReport from './register/WizardStepViewReport';

/**
 * Standalone software registration report — same content as the wizard View Report step,
 * without stage buttons / stepper chrome.
 */
export default function SoftwareReportViewPage() {
  const { id } = useParams();
  const { getItem, loadLookups, lookups } = useSoftwareService();

  const [wizardData, setWizardData] = useState(createInitialWizardState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const softwareId = id != null && id !== '' ? Number(id) : null;

  useEffect(() => {
    if (!softwareId || Number.isNaN(softwareId)) {
      setLoading(false);
      setError('Invalid software id.');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const [detail, lookupResult] = await Promise.all([
          getItem(softwareId),
          loadLookups({ force: true }),
        ]);
        if (cancelled) return;
        const regs = lookupResult?.lookups?.regulations || [];
        setWizardData(detailToWizardState(detail, regs));
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Failed to load system report.'));
          setWizardData(createInitialWizardState());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [softwareId, getItem, loadLookups]);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="System Report" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Header title="System Report" />

      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: { xs: 1.5, sm: 2 },
          mt: 1,
        }}
      >
        <WizardStepViewReport
          wizardData={wizardData}
          lookups={lookups}
          softwareId={softwareId}
        />
      </Paper>
    </Box>
  );
}
